import React, { useEffect, useId, useRef, useState } from 'react';
import { AnimatePresence, motion, useAnimationControls } from 'framer-motion';
import { EyeIcon, EyeOffIcon, LockIcon, LockOpenIcon, XIcon } from 'lucide-react';
import { Logo } from '../Logo';
import { profile } from '../../data/profile';
import { EASE_OUT } from '../../utils/motion';
type PasswordModalProps = {
  open: boolean;
  onClose: () => void;
  /** Called once the password is accepted and the success state has shown. */
  onSuccess: () => void;
  verify: (password: string) => boolean;
  description?: string;
  /** Subject line for the "Ask me" email. */
  askSubject?: string;
};
export function PasswordModal({
  open,
  ...rest
}: PasswordModalProps) {
  return <AnimatePresence data-id="element-133" data-mp="2f68jp1lazpou">{open && <PasswordDialog key="password-dialog" {...rest} data-id="element-134" data-mp="2f6i140sa8oix" />}</AnimatePresence>;
}
type PasswordDialogProps = Omit<PasswordModalProps, 'open'>;
function PasswordDialog({
  onClose,
  onSuccess,
  verify,
  description = 'This project is password protected. Pop in the secret word and come on in!',
  askSubject = 'Portfolio password'
}: PasswordDialogProps) {
  const [value, setValue] = useState('');
  const [visible, setVisible] = useState(false);
  const [error, setError] = useState(false);
  const [success, setSuccess] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const timer = useRef<number>();
  const shake = useAnimationControls();
  const titleId = useId();
  const descId = useId();
  const inputId = useId();
  const errorId = useId();
  useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const focusTimer = window.setTimeout(() => inputRef.current?.focus(), 30);
    return () => {
      window.clearTimeout(focusTimer);
      window.clearTimeout(timer.current);
      document.body.style.overflow = previousOverflow;
      previouslyFocused?.focus?.();
    };
  }, []);
  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      onClose();
      return;
    }
    if (e.key !== 'Tab' || !dialogRef.current) return;
    const focusable = dialogRef.current.querySelectorAll<HTMLElement>('button:not([disabled]), input, a[href]');
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (success) return;
    if (verify(value)) {
      setError(false);
      setSuccess(true);
      timer.current = window.setTimeout(onSuccess, 380);
      return;
    }
    setError(true);
    shake.start({
      x: [0, -8, 8, -5, 5, 0],
      transition: {
        duration: 0.28,
        ease: EASE_OUT
      }
    });
    inputRef.current?.focus();
  };
  return <motion.div className="fixed inset-0 z-[60] flex items-center justify-center bg-night/50 p-4 backdrop-blur-sm" initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.2,
    ease: EASE_OUT
  }} onMouseDown={e => {
    if (e.target === e.currentTarget) onClose();
  }} data-id="element-135" data-mp="t0utesa5043t">
      <motion.div ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby={titleId} aria-describedby={descId} onKeyDown={handleKeyDown} initial={{
      opacity: 0,
      scale: 0.96,
      y: 8
    }} animate={{
      opacity: 1,
      scale: 1,
      y: 0
    }} exit={{
      opacity: 0,
      scale: 0.96,
      y: 8
    }} transition={{
      duration: 0.2,
      ease: EASE_OUT
    }} className="relative w-full max-w-[420px] rounded-[28px] bg-canvas px-6 pb-7 pt-9 text-center shadow-2xl sm:px-9" data-id="element-136" data-mp="a9bvgzuyzg8n">
        <button type="button" onClick={onClose} aria-label="Close" className="absolute right-4 top-4 grid h-9 w-9 place-items-center rounded-full text-fg-faint transition-colors duration-150 hover:bg-surface hover:text-fg" data-id="element-137" data-mp="2qxmr9y525iku">
          <XIcon className="h-5 w-5" data-id="element-138" data-mp="34nymw70upzgq" />
        </button>

        <div className="mx-auto grid h-16 w-16 place-items-center rounded-2xl bg-coral-soft" data-id="element-139" data-mp="39m9t87rxtru7">
          <Logo className="h-11 w-11" data-id="element-140" data-mp="3elco4ze7ehog" />
        </div>

        <h2 id={titleId} className="mt-5 text-[28px] font-semibold tracking-tight text-ink" data-id="element-141" data-mp="3ejt91c4aaco9">
          Psst… it's locked
        </h2>
        <p id={descId} className="mx-auto mt-2 max-w-xs text-[15px] leading-relaxed text-fg-muted" data-id="element-142" data-mp="3il53buy3rm2s">
          {description}
        </p>

        <form onSubmit={handleSubmit} className="mt-7 text-left" noValidate data-id="element-143" data-mp="1o9qbn8lj7cke">
          <label htmlFor={inputId} className="text-[11px] font-semibold uppercase tracking-[0.12em] text-fg-faint" data-id="element-144" data-mp="1t7rsrnzed1t8">
            Password
          </label>
          <motion.div animate={shake} className="relative mt-2" data-id="element-145" data-mp="1lq3htvgbt67b">
            <input ref={inputRef} id={inputId} type={visible ? 'text' : 'password'} value={value} onChange={e => {
            setValue(e.target.value);
            if (error) setError(false);
          }} placeholder="Enter password" autoComplete="current-password" aria-invalid={error} aria-describedby={error ? errorId : undefined} className={`w-full rounded-full border bg-white py-3 pl-5 pr-12 text-[15px] text-fg placeholder:text-fg-faint transition-[border-color,box-shadow] duration-150 focus:outline-none focus:ring-4 ${error ? 'border-rust/60 focus:ring-coral-soft' : 'border-line focus:border-ink/40 focus:ring-ink-soft'}`} data-id="element-146" data-mp="1gxilon9y6h0m" />
            <button type="button" onClick={() => setVisible(v => !v)} aria-label={visible ? 'Hide password' : 'Show password'} aria-pressed={visible} className="absolute right-2 top-1/2 grid h-9 w-9 -translate-y-1/2 place-items-center rounded-full text-fg-faint transition-colors duration-150 hover:text-ink" data-id="element-147" data-mp="f755vd00scl2">
              {visible ? <EyeOffIcon className="h-[18px] w-[18px]" data-id="element-148" data-mp="booa0p7f54y3" /> : <EyeIcon className="h-[18px] w-[18px]" data-id="element-149" data-mp="3u3yqr5y8g23q" />}
            </button>
          </motion.div>

          <div className="min-h-[24px] pt-2" aria-live="polite" data-id="element-150" data-mp="2qclqtu0yj8t">
            <AnimatePresence data-id="element-151" data-mp="2rnnyp7tnixt">
              {error && <motion.p id={errorId} role="alert" initial={{
              opacity: 0,
              y: -4
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0
            }} transition={{
              duration: 0.15,
              ease: EASE_OUT
            }} className="pl-5 text-xs font-medium text-rust" data-id="element-152" data-mp="3k7nc49b67064">
                  That's not it — try again or ask me.
                </motion.p>}
            </AnimatePresence>
          </div>

          <button type="submit" disabled={value.length === 0 && !success} className="mt-2 flex w-full items-center justify-center gap-2 rounded-full bg-ink py-3.5 text-[15px] font-semibold text-white transition-[background-color,transform,opacity] duration-150 ease-out hover:bg-ink-deep active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-60" data-id="element-153" data-mp="2rlp0bivu2d11">
            <AnimatePresence mode="popLayout" initial={false} data-id="element-154" data-mp="3aighnmrtei2g">
              <motion.span key={success ? 'open' : 'closed'} initial={{
              opacity: 0,
              scale: 0.8
            }} animate={{
              opacity: 1,
              scale: 1
            }} exit={{
              opacity: 0,
              scale: 0.8
            }} transition={{
              duration: 0.15,
              ease: EASE_OUT
            }} data-id="element-155" data-mp="3o921pet08y1x">
                {success ? <LockOpenIcon className="h-[18px] w-[18px]" data-id="element-156" data-mp="3taraensr8zmu" /> : <LockIcon className="h-[18px] w-[18px]" data-id="element-157" data-mp="14gj7xxk26wmg" />}
              </motion.span>
            </AnimatePresence>
            {success ? 'Come on in' : 'Unlock the door'}
          </button>
        </form>

        <a href={`mailto:${profile.email}?subject=${encodeURIComponent(askSubject)}`} className="mt-6 inline-block text-sm text-fg-muted underline decoration-dotted decoration-fg-faint underline-offset-4 transition-colors duration-150 hover:text-ink hover:decoration-ink" data-id="element-158" data-mp="1dgftvrf2dhdt">
          Forgot the magic word? Ask me
        </a>
      </motion.div>
    </motion.div>;
}